<template>
  <section class="bg-gray-100" id="developers">
    <div class="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
      <div class="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <!-- Copy -->
        <div class="flex flex-col gap-8">
          <div class="flex flex-col gap-4">
            <span class="text-sm font-semibold uppercase tracking-wider text-primary-600">For developers</span>
            <h2 class="text-pretty text-3xl font-semibold tracking-[-0.4px] text-gray-950 sm:text-4xl sm:tracking-[-0.72px] lg:text-balance">
              One tag to embed. One call to export.
            </h2>
            <p class="text-lg text-gray-700">
              Mount the editor anywhere, pass a user token, and receive the HTML in a webhook or a single API call.
              Sandbox keys are free and never expire.
            </p>
          </div>
          <ul class="flex flex-col gap-3">
            <li
              v-for="item in checkList"
              :key="item"
              class="flex items-start gap-3 text-base text-gray-800"
            >
              <svg
                class="mt-0.5 size-5 shrink-0 text-primary-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12l5 5L20 7" />
              </svg>
              <span v-html="item" />
            </li>
          </ul>
          <div class="flex flex-wrap gap-3">
            <Link
              href="/developers"
              class="relative flex items-center gap-1.5 overflow-hidden rounded-lg bg-gradient-to-b from-primary-600 to-primary-700 px-5 py-2.5 text-sm font-semibold text-white shadow-[0px_1px_0px_0px_rgba(16,24,40,0.08)]"
            >
              <span class="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-2px_0px_0px_rgba(16,24,40,0.2)]" aria-hidden="true" />
              Read the docs
            </Link>
            <Link
              href="/signup"
              class="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-gradient-to-b from-white to-gray-50 px-5 py-2.5 text-sm font-semibold text-gray-800 shadow-[0px_1px_0px_0px_rgba(16,24,40,0.08)]"
            >
              Get sandbox keys
            </Link>
          </div>
        </div>

        <!-- Code block -->
        <div class="overflow-hidden rounded-xl border border-gray-900 bg-gray-950 shadow-[0px_20px_48px_-12px_rgba(0,0,0,0.5)]">
          <!-- Code header -->
          <div class="flex items-center justify-between border-b border-gray-900 px-4 py-3">
            <span class="font-mono text-sm text-gray-600">index.html</span>
            <button
              type="button"
              class="flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-gray-600 transition hover:bg-gray-900 hover:text-gray-400"
              @click="copyCode"
              :aria-label="copied ? 'Copied' : 'Copy code'"
            >
              <svg v-if="!copied" class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <rect x="9" y="9" width="12" height="12" rx="2" />
                <path d="M5 15V5a2 2 0 0 1 2-2h10" />
              </svg>
              <svg v-else class="size-4 text-[#34d399]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M5 12l5 5L20 7" />
              </svg>
              {{ copied ? 'Copied!' : 'Copy' }}
            </button>
          </div>
          <!-- Code body -->
          <pre class="overflow-x-auto p-5 text-sm leading-relaxed" aria-label="Embed code snippet"><code><span class="text-[#d0d5dd]">&lt;</span><span class="text-[#b692f6]">script</span> <span class="text-[#84caff]">src</span>=<span class="text-[#6ce9a6]">"https://cdn.designertool.io/v1/editor.js"</span><span class="text-[#d0d5dd]">&gt;&lt;/</span><span class="text-[#b692f6]">script</span><span class="text-[#d0d5dd]">&gt;</span>
<span class="text-[#d0d5dd]">&lt;</span><span class="text-[#b692f6]">div</span> <span class="text-[#84caff]">id</span>=<span class="text-[#6ce9a6]">"editor"</span><span class="text-[#d0d5dd]">&gt;&lt;/</span><span class="text-[#b692f6]">div</span><span class="text-[#d0d5dd]">&gt;</span>
<span class="text-[#d0d5dd]">&lt;</span><span class="text-[#b692f6]">script</span><span class="text-[#d0d5dd]">&gt;</span>
  <span class="text-[#d0d5dd]">const</span> <span class="text-[#84caff]">editor</span> = <span class="text-[#d0d5dd]">DesignerTool.</span><span class="text-[#b692f6]">mount</span>(<span class="text-[#6ce9a6]">'#editor'</span>, {
    <span class="text-[#84caff]">token</span>: <span class="text-[#6ce9a6]">'&lt;user-session-token&gt;'</span>,
    <span class="text-[#84caff]">size</span>: <span class="text-[#6ce9a6]">'4x6'</span>,
    <span class="text-[#84caff]">template</span>: <span class="text-[#6ce9a6]">'just-listed'</span>,
    <span class="text-[#84caff]">mergeFields</span>: [<span class="text-[#6ce9a6]">'first_name'</span>, <span class="text-[#6ce9a6]">'address'</span>],
    <span class="text-[#84caff]">theme</span>: { <span class="text-[#84caff]">primary</span>: <span class="text-[#6ce9a6]">'#7F56D9'</span> }
  });
  <span class="text-[#84caff]">editor</span>.<span class="text-[#b692f6]">on</span>(<span class="text-[#6ce9a6]">'export'</span>, ({ <span class="text-[#84caff]">front</span>, <span class="text-[#84caff]">back</span> }) =&gt; <span class="text-[#84caff]">sendToLob</span>(<span class="text-[#84caff]">front</span>, <span class="text-[#84caff]">back</span>));
<span class="text-[#d0d5dd]">&lt;/</span><span class="text-[#b692f6]">script</span><span class="text-[#d0d5dd]">&gt;</span></code></pre>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { Link } from "@inertiajs/vue3";

const copied = ref(false);

const checkList = [
  "Script tag, iframe or React component",
  "Webhooks for <code class=\"rounded bg-gray-200 px-1 py-0.5 font-mono text-xs text-gray-800\">design.exported</code> and <code class=\"rounded bg-gray-200 px-1 py-0.5 font-mono text-xs text-gray-800\">design.saved</code>",
  "Merge syntax matched to Lob and PostGrid",
];

const rawCode = `<script src="https://cdn.designertool.io/v1/editor.js"><\/script>
<div id="editor"></div>
<script>
  const editor = DesignerTool.mount('#editor', {
    token: '<user-session-token>',
    size: '4x6',
    template: 'just-listed',
    mergeFields: ['first_name', 'address'],
    theme: { primary: '#7F56D9' }
  });
  editor.on('export', ({ front, back }) => sendToLob(front, back));
<\/script>`;

function copyCode() {
  navigator.clipboard.writeText(rawCode).then(() => {
    copied.value = true;
    setTimeout(() => (copied.value = false), 2000);
  });
}
</script>
